import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, MapPin, ArrowLeft, RotateCcw } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { EmptyState } from "@/components/ui/empty-state";

export default function ArchivedLocationList() {
  const { id } = useParams<{ id: string }>();
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // Fetch archived locations for this business from Convex
  const archivedLocations = useQuery(api.serviceLocations.getArchivedLocationsByBusiness, id ? { businessId: id as Id<"businesses"> } : "skip") ?? [];
  const unarchiveLocation = useMutation(api.serviceLocations.unarchive);

  const filteredLocations = archivedLocations.filter(
    (location: any) =>
      location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUnarchive = async (locationId: Id<"serviceLocations">, locationName: string) => {
    try {
      await unarchiveLocation({ id: locationId });
      toast.success(`${locationName} has been unarchived successfully`);
    } catch (error) {
      console.error("Failed to unarchive location:", error);
      toast.error("Failed to unarchive location");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/clients/${id}`)}
            className="gap-2 pl-0"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Client
          </Button>
        </div>
      </div>

      <div className="mb-2">
        <h1 className="text-2xl font-semibold text-gray-900">Archived Locations</h1>
        <p className="mt-1 text-gray-500">View and manage archived locations for this client</p>
      </div>

      <Card className="p-6">
        <div className="mb-6 flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder="Search archived locations..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {filteredLocations.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mb-4 text-gray-400">
              <MapPin className="mx-auto h-12 w-12" />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">No archived locations found</h3>
            <p className="text-gray-500">
              {searchQuery
                ? "Try adjusting your search criteria"
                : "There are no archived locations for this client"}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Location</TableHead>
                <TableHead>Square Footage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLocations.map((location: any) => (
                <TableRow key={location._id}>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">{location.name}</span>
                      </div>
                      <div className="text-sm text-gray-500 ml-6">{location.address}</div>
                    </div>
                  </TableCell>
                  <TableCell>{location.squareFootage}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        location.status === "active"
                          ? "bg-green-100 text-green-700"
                          : location.status === "pending"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {(location.status || "Active").charAt(0).toUpperCase() +
                        (location.status || "Active").slice(1)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => handleUnarchive(location._id, location.name)}
                    >
                      <RotateCcw className="h-3 w-3" />
                      Unarchive
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
